import { inject, InjectionKey, reactive, ref } from "vue";
import { APIService } from "@/services/APIService.inject";
import { AuthStore } from "@/stores/modules/auth";
import {
  IAttribute,
  IAttributeGroup,
  IAuthor,
  ICampaign,
  IContract,
  IEditData,
  IImageMaterial,
  IMaintainer,
  IManageContent,
  IQuestionnaireData,
  ITemplateSet,
  ISampleSet,
  Roles,
} from "@/services/Models";

const contracts = ref([] as IContract[]);
const authors = ref([] as IAuthor[]);
const attributes = ref([] as IAttribute[]);
const attributeGroups = ref([] as IAttributeGroup[]);
const templateSets = ref([] as ITemplateSet[]);
const campaigns = ref([] as ICampaign[]);
const questionnaires = ref([] as IQuestionnaireData[]);
const imageMaterials = ref([] as IImageMaterial[]);
const editDatas = ref([] as IEditData[]);
const isLoaded = ref(false);
const maintainers = ref([] as IMaintainer[]);
const manageContent = ref<IManageContent>({ id: undefined, message: "" });
const sampleSets = ref([] as ISampleSet[]);
const sampleAttributes = ref([] as IAttribute[]);

const resourceStore = (api: APIService, auth: AuthStore) => {
  // FIXME 起動時に全件持つのやめる？
  const data = reactive({
    isLoaded,
    contracts,
    authors,
    attributes,
    attributeGroups,
    templateSets,
    sampleSets,
    sampleAttributes,
    campaigns,
    questionnaires,
    imageMaterials,
    editDatas,
    maintainers,
    manageContent,
  });
  // 契約情報取得APIを呼び出す
  const getContracts = async () => {
    try {
      contracts.value = (await api.getContracts()).sort(
        (x, y) => (x.id ?? -1) - (y.id ?? -1)
      );
    } catch {
      contracts.value = [];
    }
  };

  // 契約情報登録・更新APIを呼び出す
  const saveContract = async (contract: IContract) => {
    const newOne = await api.saveContract(contract);
    await getContracts();
    return newOne;
  };

  // 契約情報削除APIを呼び出す
  const deleteContract = async (id: number) => {
    try {
      await api.deleteContract(id);
      await getContracts();
    } catch (e) {
      console.log(e);
      return undefined;
    }
  };

  // 著者情報取得APIを呼び出す
  const getAuthors = async () => (authors.value = await api.getAuthors());

  // 属性情報取得APIを呼び出す
  const getAttributes = async () =>
    (attributes.value = await api.getAttributes());

  // 属性グループ情報取得APIを呼び出す
  const getAttributeGroups = async () =>
  (attributeGroups.value = (await api.getAttributeGroups()).sort(
    (x, y) => (x.id ?? -1) - (y.id ?? -1)
  ));
  const getTemplateSets = async () =>
    (templateSets.value = await api.getTemplateSets());

  // キャンペーン情報取得APIを呼び出す
  const getCampaigns = async () => (campaigns.value = await api.getCampaigns());

  // 画像素材取得APIを呼び出す
  const getImageMaterials = async () =>
  (imageMaterials.value = (await api.getImageMaterials()).sort(
    (x, y) => (x.id ?? -1) - (y.id ?? -1)
  ));
  const getSampleSets = async () =>
    (sampleSets.value = await api.getSampleSets());
  const getSampleAttributes = async () =>
    (sampleAttributes.value = await api.getSampleAttributes());
  // 画像素材アップロードAPIを呼び出す
  const uploadUserImageMaterial = async (
    base64: string,
    name?: string
  ): Promise<void> => {
    await api.uploadUserImageMaterial(base64, name);
    await getImageMaterials();
  };

  // 画像素材削除APIを呼び出す
  const deleteUserImageMaterial = async (id: number) => {
    try {
      await api.deleteImageMaterial(id);
      await getImageMaterials();
    } catch (e) {
      console.log(e);
    }
  };

  // 編集データ一覧取得APIを呼び出す
  const getEditDatas = async () =>
  (editDatas.value = (await api.getEditDatas()).sort(
    (x, y) => (x.id ?? -1) - (y.id ?? -1)
  ));

  // 編集データ取得APIを呼び出す
  const getEditData = async (editID: number, _: number) => {
    // サーバにデータがあればそれを返す
    // const serverData = editDatas.value.find((x) => x.id == editID);
    const serverData = await api.getEditData(editID).catch(() => undefined);
    if (serverData !== undefined) {
      console.log("from server data");
      // ローカルデータも更新
      editDatas.value = editDatas.value.filter((x) => x.id != editID);
      editDatas.value.push(serverData);
      editDatas.value = editDatas.value.sort((x, y) => (x.id ?? -1) - (y.id ?? -1));
      return JSON.parse(JSON.stringify(serverData)) as IEditData;
    }
    // // なければローカルストレージの最新データを返す
    // const template = templateSets.value?.find(x => x.id == templateID);
    // if (template === undefined) return;
    // const localEditID = `local_${template.id}`;
    // const loadJson = localStorage.getItem(localEditID);
    // const loadEditData = !loadJson
    //   ? undefined
    //   : (JSON.parse(loadJson) as IEditData);
    // console.log("from local data");
    return undefined;
  };

  // 編集データ登録APIを呼び出す
  const saveEditData = async (editData: IEditData) => {
    try {
      // 同じ編集データタイトルが存在する場合は末尾に(1)等をつける
      if (isExist(editData.title, editData.id)) {
        const titleStr: string = editData.title;
        const length = data.editDatas.filter(
          (x) => x.title.includes(titleStr) && x.id !== editData.id
        ).length;
        for (let i = 1; i <= length; i++) {
          const newName = `${titleStr}(${i})`;
          if (isExist(newName, editData.id)) {
            continue;
          }
          editData.title = newName;
        }
      }
      const newOne = await api.saveEditData(editData);
      console.log("save to server");
      await getEditDatas();
      return newOne;
    } catch (e) {
      console.log(e);
      return undefined;
      // const json = JSON.stringify(editData);
      // const editID = `local_${editData.templateID}`;
      // localStorage.setItem(editID, json);
      // console.log("save to local");
    }
  };

  // 編集データ削除APIを呼び出す
  const deleteEditData = async (id: number) => {
    try {
      await api.deleteEditData(id);
      await getEditDatas();
    } catch (e) {
      console.log(e);
      return undefined;
    }
  };

  // 管理者情報取得APIを呼び出す
  const getMaintainers = async () =>
    (maintainers.value = await api.getMaintainers());
  const saveMaintainers = async (xs: IMaintainer[]) => {
    const newOne = await api.saveMaintainers(xs);
    await getMaintainers();
    return newOne;
  };

  // メンテナンス情報取得APIを呼び出す
  const getManageContent = async () =>
    (manageContent.value = await api.getManageContent());
  const saveManageContent = async (x: IManageContent) => {
    const newOne = await api.saveManageContent(x);
    await getManageContent();
    return newOne;
  };

  // 初期化(ログインユーザーが管理者かどうかで実行できるAPIを変える)
  const init = async (): Promise<void> => {
    if (auth.state.iam?.role == Roles.Admin) {
      console.log("init admin");
      await getContracts();
      await getMaintainers();
      await getManageContent();
    } else {
      console.log("init user");
      await getManageContent();
      await getAuthors();
      await getAttributes();
      await getAttributeGroups();
      await getTemplateSets();
      await getSampleSets();
      await getSampleAttributes();
      await getCampaigns();
      await getImageMaterials();
      await getEditDatas();
    }
    isLoaded.value = true;
  };
  // ストア情報をクリアする
  const clear = async (): Promise<void> => {
    isLoaded.value = false;
    contracts.value = [];
    authors.value = [];
    attributes.value = [];
    attributeGroups.value = [];
    templateSets.value = [];
    campaigns.value = [];
    questionnaires.value = [];
    imageMaterials.value = [];
    editDatas.value = [];
    maintainers.value = [];
    manageContent.value = {
      message: "",
      id: undefined,
    };
  };
  // 編集データのタイトルが既に存在していないか確認
  const isExist = (title: string, id: number | undefined) => {
    const exists = data.editDatas.find((x) => x.title == title && x.id !== id);
    return exists !== undefined;
  };

  return {
    data,
    init,
    clear,
    getContracts,
    saveContract,
    deleteContract,
    getAuthors,
    getAttributes,
    getAttributeGroups,
    getTemplateSets,
    getCampaigns,
    getImageMaterials,
    uploadUserImageMaterial,
    deleteUserImageMaterial,
    getEditDatas,
    getEditData,
    saveEditData,
    deleteEditData,
    getMaintainers,
    saveMaintainers,
    getManageContent,
    saveManageContent,
  };
};

export default resourceStore;

export type ResourceStore = ReturnType<typeof resourceStore>;

export const resourceStoreKey: InjectionKey<ResourceStore> =
  Symbol("resourceStore");

export const useResource = () => {
  const store = inject(resourceStoreKey);
  if (!store) {
    throw new Error(`${resourceStoreKey} is not provided`);
  }
  return store;
};
